import { Controller } from "@hotwired/stimulus";
import {
  errorDetail,
  getAllJSON,
  getJSON,
  listResults,
  sendJSON,
} from "../api.js";

// Bezpieczne dodawanie podkategorii (personel): wymusza „najpierw przeszukaj",
// wykrywa podobne (fuzzy, backend pg_trgm) i blokuje duplikat bez jawnego potwierdzenia.
// - autocomplete: wpisywanie nazwy → GET ?q=&category= → istniejące dopasowania pod inputem,
// - create: POST {category, name} (bez slug — backend auto-slug); 409 z `suggestions` → przycisk
//   „Utwórz mimo to" re-POSTuje z confirm=true; 409 z `existing` → „już istnieje",
// - lista + przełącznik `active` (ten sam <template> co panel słowników).
export default class extends Controller {
  static targets = [
    "rows",
    "status",
    "categorySelect",
    "nameInput",
    "suggestions",
    "formError",
    "searchInput", // filtr listy (osobny od autocomplete w formularzu dodawania)
    "prevBtn",
    "nextBtn",
    "pageInfo",
    "submitBtn", // przycisk formularza (zmienia etykietę w trybie edycji)
    "cancel", // „Anuluj" — wyjście z trybu edycji
  ];
  static values = {
    url: String, // /api/subcategories/
    categoriesUrl: String, // /api/categories/
    templateId: String, // id <template> wiersza
    canWrite: Boolean, // czy pokazywać akcje zapisu (przy 403 = false)
  };
  static SEARCH_DEBOUNCE_MS = 180;

  connect() {
    this.pending = null;
    this.searchTimer = null;
    this.listTimer = null;
    this.page = 1; // aktualna strona listy (DRF page_size 25)
    this.listQuery = ""; // fraza filtra listy
    this._editId = null; // id edytowanej podkategorii (null = tryb dodawania)
    this._createLabel = this.hasSubmitBtnTarget
      ? this.submitBtnTarget.textContent
      : "Dodaj podkategorię";
    this.load();
    this.#loadCategories();
  }

  disconnect() {
    clearTimeout(this.searchTimer);
    clearTimeout(this.listTimer);
  }

  async load() {
    let data;
    try {
      data = await getJSON(this.#listUrl());
    } catch {
      this.#status("Nie udało się załadować podkategorii. Odśwież stronę.");
      return;
    }
    this._next = data?.next ?? null;
    this._prev = data?.previous ?? null;
    this._count = data?.count ?? 0;
    const items = listResults(data);
    this.rowsTarget.textContent = "";
    this.#updatePagination();
    if (!items.length) {
      this.#status(this.listQuery ? "Brak wyników wyszukiwania." : "Brak podkategorii.");
      return;
    }
    this.#status(null);
    const template = document.getElementById(this.templateIdValue);
    for (const item of items) {
      this.rowsTarget.appendChild(this.#renderRow(template, item));
    }
  }

  // Filtr listy (debounce): zmiana frazy resetuje na stronę 1.
  filterList() {
    clearTimeout(this.listTimer);
    this.listTimer = setTimeout(() => {
      this.listQuery = this.hasSearchInputTarget
        ? this.searchInputTarget.value.trim()
        : "";
      this.page = 1;
      this.load();
    }, this.constructor.SEARCH_DEBOUNCE_MS);
  }

  nextPage() {
    if (!this._next) return;
    this.page += 1;
    this.load();
  }

  prevPage() {
    if (!this._prev || this.page <= 1) return;
    this.page -= 1;
    this.load();
  }

  // Autocomplete: debounced GET ?q=&category= → istniejące dopasowania (wymusza przeszukanie).
  search() {
    clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(
      () => this.#runSearch(),
      this.constructor.SEARCH_DEBOUNCE_MS,
    );
  }

  async create(event) {
    event.preventDefault();
    // Ten sam formularz obsługuje dodawanie i edycję — w trybie edycji idzie PATCH.
    if (this._editId != null) {
      await this.#submitEdit();
      return;
    }
    const payload = this.#payload();
    if (payload === null) return;
    await this.#submit(payload, false);
  }

  async confirmCreate() {
    if (this.pending === null) return;
    await this.#submit(this.pending, true);
  }

  // Wejście w tryb edycji: wypełnia formularz (kategoria + nazwa) danymi wiersza.
  edit(item) {
    this._editId = item.id;
    if (this.hasCategorySelectTarget) {
      this.categorySelectTarget.value = String(item.category);
    }
    this.nameInputTarget.value = item.name;
    this.pending = null;
    this.#clearSuggestions();
    this.#formError(null);
    if (this.hasSubmitBtnTarget) this.submitBtnTarget.textContent = "Zapisz zmiany";
    if (this.hasCancelTarget) this.cancelTarget.hidden = false;
    this.nameInputTarget.focus();
  }

  // „Anuluj" — wyjście z edycji bez zapisu.
  cancelEdit() {
    this.#exitEdit();
    this.nameInputTarget.value = "";
    this.#clearSuggestions();
    this.#formError(null);
  }

  // --- prywatne ---

  // URL listy: kanoniczny bez parametrów dla strony 1 bez filtra; inaczej ?q=&page=.
  #listUrl() {
    const params = new URLSearchParams();
    if (this.listQuery) params.set("q", this.listQuery);
    if (this.page > 1) params.set("page", String(this.page));
    const qs = params.toString();
    return qs ? `${this.urlValue}?${qs}` : this.urlValue;
  }

  #updatePagination() {
    if (this.hasPageInfoTarget) {
      this.pageInfoTarget.textContent = `strona ${this.page} (${this._count})`;
    }
    if (this.hasPrevBtnTarget) this.prevBtnTarget.disabled = !this._prev;
    if (this.hasNextBtnTarget) this.nextBtnTarget.disabled = !this._next;
  }

  #payload() {
    const category = Number(this.categorySelectTarget.value);
    const name = this.nameInputTarget.value.trim();
    if (!category || !name) {
      this.#formError("Wybierz kategorię i podaj nazwę podkategorii.");
      return null;
    }
    this.#formError(null);
    return { category, name };
  }

  // PATCH edycji: {category, name} → regeneracja slug + dedup po stronie backendu.
  async #submitEdit() {
    const payload = this.#payload();
    if (payload === null) return;
    const res = await sendJSON("PATCH", `${this.urlValue}${this._editId}/`, payload);
    if (res.ok) {
      this.#exitEdit();
      this.nameInputTarget.value = "";
      await this.load();
      return;
    }
    if (res.status === 409 && res.data?.existing) {
      this.#renderExisting(res.data.existing);
      return;
    }
    this.#formError(errorDetail(res.data, "Nie udało się zapisać zmian."));
  }

  #exitEdit() {
    this._editId = null;
    if (this.hasSubmitBtnTarget) this.submitBtnTarget.textContent = this._createLabel;
    if (this.hasCancelTarget) this.cancelTarget.hidden = true;
  }

  async #submit(payload, confirm) {
    const body = confirm ? { ...payload, confirm: true } : payload;
    const res = await sendJSON("POST", this.urlValue, body);
    if (res.ok) {
      this.pending = null;
      this.#clearSuggestions();
      this.nameInputTarget.value = "";
      await this.load();
      return;
    }
    if (res.status === 409 && res.data?.suggestions) {
      this.pending = payload;
      this.#renderSuggestions(res.data.suggestions);
      return;
    }
    if (res.status === 409 && res.data?.existing) {
      this.pending = null;
      this.#renderExisting(res.data.existing);
      return;
    }
    this.#formError(errorDetail(res.data, "Nie udało się zapisać podkategorii."));
  }

  async #runSearch() {
    const term = this.nameInputTarget.value.trim();
    if (!term) {
      this.#clearSuggestions();
      return;
    }
    const params = new URLSearchParams();
    const category = this.categorySelectTarget.value;
    if (category) params.set("category", category);
    params.set("q", term);
    let data;
    try {
      data = await getJSON(`${this.urlValue}?${params}`);
    } catch {
      return;
    }
    const matches = listResults(data);
    this.#renderMatches(matches);
  }

  #renderMatches(matches) {
    const box = this.suggestionsTarget;
    box.textContent = "";
    if (!matches.length) {
      box.hidden = true;
      return;
    }
    box.appendChild(this.#paragraph("Istniejące podobne:"));
    box.appendChild(this.#nameList(matches, (m) => m.name));
    box.hidden = false;
  }

  #renderSuggestions(suggestions) {
    const box = this.suggestionsTarget;
    box.textContent = "";
    box.appendChild(
      this.#paragraph(
        "Znaleziono podobne podkategorie — upewnij się, że nie tworzysz duplikatu:",
      ),
    );
    box.appendChild(
      this.#nameList(suggestions, (s) =>
        s.similarity != null
          ? `${s.name} (${Math.round(s.similarity * 100)}%)`
          : s.name,
      ),
    );
    const button = document.createElement("button");
    button.type = "button";
    button.className = "ls-btn ls-btn--sm";
    button.setAttribute("data-subcategory-admin-confirm", "");
    button.textContent = "Utwórz mimo to (nowa)";
    button.addEventListener("click", () => this.confirmCreate());
    box.appendChild(button);
    box.hidden = false;
  }

  #renderExisting(existing) {
    const box = this.suggestionsTarget;
    box.textContent = "";
    box.appendChild(
      this.#paragraph(`Taka podkategoria już istnieje: ${existing.name}.`),
    );
    box.hidden = false;
  }

  #renderRow(template, item) {
    const row = template.content.firstElementChild.cloneNode(true);
    for (const cell of row.querySelectorAll("[data-field]")) {
      const value = item[cell.dataset.field];
      cell.textContent =
        value === true ? "tak" : value === false ? "nie" : (value ?? "—");
    }
    const edit = row.querySelector("[data-subcategory-admin-edit]");
    if (edit) {
      if (!this.canWriteValue) {
        edit.hidden = true;
      } else {
        edit.hidden = false;
        edit.textContent = "Edytuj";
        edit.addEventListener("click", () => this.edit(item));
      }
    }
    const toggle = row.querySelector("[data-subcategory-admin-toggle]");
    if (toggle) {
      if (!this.canWriteValue) {
        toggle.hidden = true;
      } else {
        toggle.hidden = false;
        toggle.textContent = item.active ? "Dezaktywuj" : "Aktywuj";
        toggle.addEventListener("click", () => this.#toggle(item));
      }
    }
    return row;
  }

  async #toggle(item) {
    const res = await sendJSON("PATCH", `${this.urlValue}${item.id}/`, {
      active: !item.active,
    });
    if (!res.ok) {
      this.#status(errorDetail(res.data, "Nie udało się zmienić statusu."));
      return;
    }
    await this.load();
  }

  async #loadCategories() {
    if (!this.hasCategorySelectTarget || !this.categoriesUrlValue) return;
    let options;
    try {
      options = await getAllJSON(this.categoriesUrlValue);
    } catch {
      return;
    }
    for (const option of options) {
      const el = document.createElement("option");
      el.value = option.id;
      el.textContent = option.name;
      this.categorySelectTarget.appendChild(el);
    }
  }

  #paragraph(text) {
    const p = document.createElement("p");
    p.textContent = text;
    return p;
  }

  #nameList(items, label) {
    const ul = document.createElement("ul");
    for (const item of items) {
      const li = document.createElement("li");
      li.textContent = label(item);
      ul.appendChild(li);
    }
    return ul;
  }

  #clearSuggestions() {
    if (!this.hasSuggestionsTarget) return;
    this.suggestionsTarget.textContent = "";
    this.suggestionsTarget.hidden = true;
  }

  #formError(message) {
    if (!this.hasFormErrorTarget) return;
    this.formErrorTarget.hidden = message === null;
    if (message !== null) this.formErrorTarget.textContent = message;
  }

  #status(message) {
    if (!this.hasStatusTarget) return;
    this.statusTarget.hidden = message === null;
    if (message !== null) this.statusTarget.textContent = message;
  }
}
